import { useState, useMemo, useEffect } from "react";
import { useNavigate, useBlocker } from "react-router-dom";
import { ArrowLeft, Plus, ArrowUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InlineLoader, FullPageLoader } from "@/components/ui/pulsing-loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { RequestMetadataCard, RequestActionsBar } from "../components/molecules";
import { AdminEditToggle } from "../components/atoms/AdminEditToggle";
import { RemoveMinisterDialog, RejectRequestDialog, UnsavedChangesDialog, DropdownChangeConfirmDialog } from "../components/organisms";
import { StatusBadge } from "@/components/atoms/StatusBadge";
import { MinisterFilterState, getMinisterFilterOptions } from "../types/minister-filter-state";
import type { CredentialRequest, Minister, MinisterAvailability } from "../types";
import type { District } from "@/models/district";
import type { Region } from "@/models/region";
import { type ColumnDef } from "@tanstack/react-table";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableView } from "@/components/organisms";

interface CredentialRequestDetailTemplateProps {
  // Request data
  request: CredentialRequest | null;
  ministers: Minister[];
  districtName: string;
  regionName: string;
  isNewRequest: boolean;
  year: number;
  isInstitutionRequest?: boolean;
  isFunctionRequest?: boolean;
  isNacionalFunctionRequest?: boolean;
  
  // Dropdowns data
  districts: District[];
  regions: Region[];
  availableYears: number[];
  
  // Dropdown selection state
  selectedDistrictId: string | null;
  selectedRegionId: string | null;
  selectedYear: number | null;
  onDistrictChange: (districtId: string) => void;
  onRegionChange: (regionId: string) => void;
  onYearChange: (year: number) => void;
  
  // User permissions
  canEdit: boolean;
  isAdmin: boolean;
  
  // Loading/submission states
  isLoadingRequest: boolean;
  isLoadingMinisters: boolean;
  isSubmitting: boolean;
  
  // Minister management
  availableMinisters: MinisterAvailability[];
  selectedMinisterIds: Set<string>;
  onMinisterSelectionChange: (ministerId: string, selected: boolean) => Promise<void>;
  onRemoveMinister: (ministerId: string) => Promise<void>;
  
  // Request actions
  onSubmit: () => Promise<void>;
  onSave?: () => Promise<void>;
  onCreateAndSubmit?: () => Promise<void>;
  onApprove: () => Promise<void>;
  onReject: (notes: string) => Promise<void>;
  onMarkPrinted: () => Promise<void>;
  onMarkDelivered: () => Promise<void>;
  
  // Notes field
  notes: string;
  onNotesChange: (notes: string) => void;
  
  // Code field (admin only)
  code: string;
  onCodeChange: (code: string) => void;
  onSaveCodeAndNotes?: () => void;
  isSavingCode?: boolean;
  suggestedCodeNumber?: number;
  
  // Change tracking
  hasUnsavedChanges?: boolean;
  pendingChanges?: {
    notesChanged: boolean;
    codeChanged: boolean;
    ministersAdded: number;
    ministersRemoved: number;
  };
  
  // Admin edit mode
  isAdminEditMode?: boolean;
  onAdminEditModeChange?: (enabled: boolean) => void;
  onSaveAdminChanges?: () => void;
  isSavingAdminChanges?: boolean;
}

/**
 * Template: New layout for credential request detail page
 * Inline dropdowns + unified minister grid with filter toggle
 */
export function CredentialRequestDetailTemplate({
  request,
  ministers,
  districtName,
  regionName,
  isNewRequest,
  year,
  isInstitutionRequest = false,
  isFunctionRequest = false,
  isNacionalFunctionRequest = false,
  districts,
  regions,
  availableYears,
  selectedDistrictId,
  selectedRegionId,
  selectedYear,
  onDistrictChange,
  onRegionChange,
  onYearChange,
  canEdit,
  isAdmin,
  isLoadingRequest,
  isLoadingMinisters,
  isSubmitting,
  availableMinisters,
  selectedMinisterIds,
  onMinisterSelectionChange,
  onRemoveMinister,
  onSubmit,
  onSave,
  onCreateAndSubmit,
  onApprove,
  onReject,
  onMarkPrinted,
  onMarkDelivered,
  notes,
  onNotesChange,
  code,
  onCodeChange,
  onSaveCodeAndNotes,
  isSavingCode = false,
  hasUnsavedChanges = false,
  suggestedCodeNumber,
  pendingChanges = {
    notesChanged: false,
    codeChanged: false,
    ministersAdded: 0,
    ministersRemoved: 0,
  },
  isAdminEditMode = false,
  onAdminEditModeChange,
  onSaveAdminChanges,
  isSavingAdminChanges = false,
}: CredentialRequestDetailTemplateProps) {
  // Navigation
  const navigate = useNavigate();
  
  // Compute effective edit ability - regular canEdit OR admin edit mode enabled
  const effectiveCanEdit = canEdit || (isAdmin && isAdminEditMode);
  
  // Dialog states
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);
  const [dropdownChangeDialogOpen, setDropdownChangeDialogOpen] = useState(false);
  const [ministerToRemove, setMinisterToRemove] = useState<Minister | null>(null);
  const [pendingDropdownChange, setPendingDropdownChange] = useState<{
    type: 'district' | 'region' | 'year';
    value: string | number;
  } | null>(null);
  
  // Block navigation when there are unsaved changes
  const shouldBlock = hasUnsavedChanges && request && ['DRAFT', 'REJECTED'].includes(request.status || '');
  
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (shouldBlock && currentLocation.pathname !== nextLocation.pathname) {
      setUnsavedChangesDialogOpen(true);
      return true;
    }
    return false;
  });
  
  // Handle unsaved changes dialog confirmation
  const handleUnsavedChangesConfirm = () => {
    setUnsavedChangesDialogOpen(false);
    if (blocker.state === "blocked") {
      blocker.proceed();
    }
  };
  
  const handleUnsavedChangesCancel = () => {
    setUnsavedChangesDialogOpen(false);
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  };
  
  // Filter state - default based on request status and edit mode
  const [filterState, setFilterState] = useState<MinisterFilterState>(() => {
    // For active requests (not DRAFT/REJECTED) and not in edit mode, show only selected ministers
    if (request && request.status && !['DRAFT', 'REJECTED'].includes(request.status) && !isAdminEditMode) {
      return 'selected';
    }
    // For new/draft/rejected requests, or admin edit mode, default to all
    return 'all';
  });
  
  // Search state for DataTableView
  const [searchValue, setSearchValue] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Degree filter state (only for editable requests)
  const [selectedDegreeId, setSelectedDegreeId] = useState<string | null>(null);
  
  // District and Region filter state (for INSTITUTION/Function requests that don't have district/region in request)
  const [selectedFilterDistrictId, setSelectedFilterDistrictId] = useState<string | null>(null);
  const [selectedFilterRegionId, setSelectedFilterRegionId] = useState<string | null>(null);
  
  // Load regions for district filter (for INSTITUTION/Function requests)
  const [filterRegions, setFilterRegions] = useState<Region[]>([]);
  
  useEffect(() => {
    if (selectedFilterDistrictId && (isInstitutionRequest || isFunctionRequest)) {
      // Load regions for the selected district from districts array
      const selectedDistrict = districts.find(d => d.id === selectedFilterDistrictId);
      if (selectedDistrict?.regions) {
        setFilterRegions(selectedDistrict.regions);
      } else {
        setFilterRegions([]);
      }
    } else {
      setFilterRegions([]);
      setSelectedFilterRegionId(null);
    }
  }, [selectedFilterDistrictId, districts, isInstitutionRequest, isFunctionRequest]);
  
  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterState, searchValue, selectedDegreeId, selectedFilterDistrictId, selectedFilterRegionId]);
  
  // Update filter state when admin edit mode changes
  useEffect(() => {
    if (request && request.status && !['DRAFT', 'REJECTED'].includes(request.status)) {
      // When admin edit mode is toggled on locked requests
      if (isAdminEditMode) {
        setFilterState('all'); // Show all ministers when entering edit mode
      } else {
        setFilterState('selected'); // Show only selected when exiting edit mode
      }
    }
  }, [isAdminEditMode, request]);
  
  // Scroll to top button state
  const [showScrollTop, setShowScrollTop] = useState(false);
  
  // Track scroll position for scroll-to-top button (listen to main element)
  useEffect(() => {
    const handleScroll = () => {
      const mainElement = document.querySelector('main');
      const scrolled = mainElement ? mainElement.scrollTop > 200 : false;
      setShowScrollTop(scrolled);
    };
    
    // Listen to main element scroll
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll);
    }
    
    return () => {
      if (mainElement) {
        mainElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);
  
  // Scroll to top handler (scroll main element to top)
  const scrollToTop = () => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Determine if segmented control should be shown
  // Show for new requests, draft/rejected requests, OR when admin edit mode is active
  const showSegmentedControl = isNewRequest || effectiveCanEdit;
  
  // Check if all required dropdowns are filled
  const allDropdownsFilled = (isInstitutionRequest || isNacionalFunctionRequest)
    ? !!selectedYear
    : !!(selectedDistrictId && selectedRegionId && selectedYear);
  
  // Get unique degrees from available ministers (for filter dropdown)
  const uniqueDegrees = useMemo(() => {
    const degreesMap = new Map<string, { id: string; name: string }>();
    availableMinisters.forEach(m => {
      if (m.degree?.id && m.degree?.name) {
        degreesMap.set(m.degree.id, { id: m.degree.id, name: m.degree.name });
      }
    });
    return Array.from(degreesMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [availableMinisters]);

  // Filter ministers based on selected filter state
  const filteredMinisters = useMemo(() => {
    if (!allDropdownsFilled) return [];
    
    let filtered: MinisterAvailability[] = [];
    
    // For non-editable requests (SUBMITTED, APPROVED, PRINTED, DELIVERED) and NOT in admin edit mode,
    // show ministers directly from the request
    if (request && !effectiveCanEdit) {
      // Convert Minister[] to MinisterAvailability[] format for display
      filtered = ministers.map(m => ({
        ...m,
        availability_status: 'TAKEN' as const,
        current_request_codes: [request.code || ''],
      }));
    } else {
      // For editable requests (NEW, DRAFT, REJECTED) OR admin edit mode, apply availability filtering
      switch (filterState) {
        case 'all':
          filtered = availableMinisters;
          break;
        case 'selected':
          // Include both selected ministers AND ministers already in the request
          filtered = availableMinisters.filter(m => 
            selectedMinisterIds.has(m.id) || 
            (request && ministers.some(rm => rm.id === m.id))
          );
          break;
        case 'available':
          // Show ministers that are available OR locally selected (pending adds)
          filtered = availableMinisters.filter(m => {
            const isInSavedRequest = request && ministers.some(rm => rm.id === m.id);
            const isPendingAdd = selectedMinisterIds.has(m.id) && !isInSavedRequest;
            const isAvailableAndNotSelected = !selectedMinisterIds.has(m.id) && m.availability_status === 'AVAILABLE';
            
            return !isInSavedRequest && (isPendingAdd || isAvailableAndNotSelected);
          });
          break;
        default:
          filtered = availableMinisters;
      }
    }
    
    // For FUNCTION requests, filter to show only authorities (ministers with a position)
    if (isFunctionRequest) {
      // Only show ministers who have a position (authorities)
      filtered = filtered.filter(m => m.position && m.position.trim() !== '');
    }
    
    // Apply token-based search filter to all requests (AND logic)
    if (searchValue.trim()) {
      const searchTokens = searchValue.toLowerCase().trim().split(/\s+/);
      filtered = filtered.filter(m => {
        // Normalize ADB code by removing dashes for flexible matching
        const normalizedCode = (m.adb_code || "").replace(/-/g, "");
        const searchableText = `${m.full_name || ""} ${m.adb_code || ""} ${normalizedCode} ${m.dni || ""}`.toLowerCase();
        return searchTokens.every(token => searchableText.includes(token));
      });
    }
    
    // Apply degree filter to all requests
    if (selectedDegreeId) {
      filtered = filtered.filter(m => m.degree?.id === selectedDegreeId);
    }
    
    // Apply district filter (for INSTITUTION/Function requests)
    if (selectedFilterDistrictId && (isInstitutionRequest || isFunctionRequest)) {
      filtered = filtered.filter(m => m.district?.id === selectedFilterDistrictId);
    }
    
    // Apply region filter (for INSTITUTION/Function requests)
    if (selectedFilterRegionId && (isInstitutionRequest || isFunctionRequest)) {
      filtered = filtered.filter(m => m.region?.id === selectedFilterRegionId);
    }
    
    return filtered;
  }, [availableMinisters, filterState, selectedMinisterIds, allDropdownsFilled, request, ministers, searchValue, selectedDegreeId, selectedFilterDistrictId, selectedFilterRegionId, effectiveCanEdit, isInstitutionRequest, isFunctionRequest]);

  // Calculate paginated data (only for INSTITUTION requests)
  const paginatedMinisters = useMemo(() => {
    // Only paginate for INSTITUTION requests, show all for MINISTERIAL and FUNCTION
    if (!isInstitutionRequest) {
      return filteredMinisters;
    }
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredMinisters.slice(startIndex, endIndex);
  }, [filteredMinisters, currentPage, pageSize, isInstitutionRequest]);
  
  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(filteredMinisters.length / pageSize);
  }, [filteredMinisters.length, pageSize]);
  
  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Define table columns
  const columns = useMemo<ColumnDef<MinisterAvailability>[]>(() => {
    const baseColumns: ColumnDef<MinisterAvailability>[] = [];
    
    // Add row number column
    baseColumns.push({
      id: 'rowNumber',
      header: '#',
      cell: ({ row }) => {
        const rowNumber = (currentPage - 1) * pageSize + row.index + 1;
        return <div className="text-center text-sm text-muted-foreground">{rowNumber}</div>;
      },
      size: 50,
    });
    
    // Only show selection/checkbox column for editable requests
    if (effectiveCanEdit) {
      baseColumns.push({
        id: 'select',
        header: '',
        cell: ({ row }) => {
          const minister = row.original;
          const isSelected = selectedMinisterIds.has(minister.id);
          const isTaken = minister.availability_status === 'TAKEN';
          
          // Check if minister is in the current request being edited
          const isInCurrentRequest = request && ministers.some(m => m.id === minister.id);
          
          // Only show "taken" label if minister is in ANOTHER request (not the current one)
          if (isTaken && !isInCurrentRequest) {
            // Minister is in a different active request - show message instead of checkbox
            const codes = minister.current_request_codes && minister.current_request_codes.length > 0
              ? minister.current_request_codes.join(', ')
              : 'En otra solicitud';
            return (
              <div className="text-xs text-muted-foreground">
                En solicitud: {codes}
              </div>
            );
          }
          
          // Show checkbox for available ministers OR ministers in the current request
          return (
            <div className="flex items-center justify-center">
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => {
                  onMinisterSelectionChange(minister.id, checked as boolean);
                }}
                aria-label="Seleccionar ministro"
                disabled={isSubmitting}
              />
            </div>
          );
        },
        size: 200,
      });
    }
    
    // Add standard columns
    baseColumns.push({
      accessorKey: 'adb_code',
      header: 'Código ADB',
      size: 120,
    });
    
    baseColumns.push({
      accessorKey: 'full_name',
      header: 'Nombre Completo',
      size: 250,
    });
    
    baseColumns.push({
      accessorKey: 'dni',
      header: 'CI',
      size: 100,
    });
    
    baseColumns.push({
      id: 'degree',
      header: 'Grado',
      accessorFn: (row) => row.degree?.name || '-',
      size: 150,
    });
    
    // Add district column if not filtered by district (showing region view)
    if (!selectedDistrictId) {
      baseColumns.push({
        id: 'district',
        header: 'Distrito',
        accessorFn: (row) => row.district?.name || '-',
        size: 150,
      });
    }
    
    // Add region column if not filtered by region
    if (!selectedRegionId && selectedDistrictId) {
      baseColumns.push({
        id: 'region',
        header: 'Región',
        accessorFn: (row) => row.region?.name || '-',
        size: 150,
      });
    }
    
    return baseColumns;
  }, [selectedMinisterIds, onMinisterSelectionChange, isSubmitting, selectedDistrictId, selectedRegionId, effectiveCanEdit, request, ministers, currentPage, pageSize]);

  // Row click handler for selection
  const handleRowClick = (minister: MinisterAvailability) => {
    // Only allow selection if editable and minister is not taken
    if (effectiveCanEdit && minister.availability_status !== 'TAKEN') {
      const isSelected = selectedMinisterIds.has(minister.id);
      onMinisterSelectionChange(minister.id, !isSelected);
    }
  };

  // Action handlers
  const handleRemoveMinister = async () => {
    if (!ministerToRemove) return;
    
    await onRemoveMinister(ministerToRemove.id);
    setRemoveDialogOpen(false);
    setMinisterToRemove(null);
  };

  const handleRejectRequest = async (notes: string) => {
    await onReject(notes);
    setRejectDialogOpen(false);
  };
  
  // Dropdown change handlers with confirmation for new requests
  const handleDistrictChangeWithConfirm = (districtId: string) => {
    if (isNewRequest && selectedMinisterIds.size > 0) {
      setPendingDropdownChange({ type: 'district', value: districtId });
      setDropdownChangeDialogOpen(true);
    } else {
      onDistrictChange(districtId);
    }
  };
  
  const handleRegionChangeWithConfirm = (regionId: string) => {
    if (isNewRequest && selectedMinisterIds.size > 0) {
      setPendingDropdownChange({ type: 'region', value: regionId });
      setDropdownChangeDialogOpen(true);
    } else {
      onRegionChange(regionId);
    }
  };
  
  const handleYearChangeWithConfirm = (year: number) => {
    if (isNewRequest && selectedMinisterIds.size > 0) {
      setPendingDropdownChange({ type: 'year', value: year });
      setDropdownChangeDialogOpen(true);
    } else {
      onYearChange(year);
    }
  };
  
  const handleDropdownChangeConfirm = () => {
    if (pendingDropdownChange) {
      switch (pendingDropdownChange.type) {
        case 'district':
          onDistrictChange(pendingDropdownChange.value as string);
          break;
        case 'region':
          onRegionChange(pendingDropdownChange.value as string);
          break;
        case 'year':
          onYearChange(pendingDropdownChange.value as number);
          break;
      }
    }
    setDropdownChangeDialogOpen(false);
    setPendingDropdownChange(null);
  };

  // Show loading state
  if (isLoadingRequest) {
    return (
      <div className="flex h-64 items-center justify-center">
        <InlineLoader message="Cargando solicitud..." />
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isAdminEditMode ? 'rounded-lg border-2 border-amber-300 p-4 dark:border-amber-700' : ''}`}>
      {/* Back button and status badge */}
      <div className="mb-6 flex items-center justify-between">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="hover:bg-accent"
        >
          <ArrowLeft className="mr-2 size-4" />
          Volver a la lista
        </Button>
        <div className="flex items-center gap-3">
          {/* Admin Edit Mode Toggle - only show for non-editable states */}
          {request && isAdmin && !canEdit && onAdminEditModeChange && ['SUBMITTED', 'APPROVED', 'PRINTED', 'DELIVERED'].includes(request.status || '') && (
            <AdminEditToggle
              enabled={isAdminEditMode}
              onToggle={onAdminEditModeChange}
              disabled={isSubmitting || isSavingAdminChanges}
            />
          )}
          {request && <StatusBadge status={request.status} variant="large" showIcon />}
        </div>
      </div>

      {/* Request metadata - show for both existing and new requests */}
      {(request || isNewRequest) && (
        <RequestMetadataCard
          request={request}
          year={request?.year || year}
          districtName={districtName}
          regionName={regionName}
          ministerCount={ministers.length}
          notes={notes}
          onNotesChange={onNotesChange}
          canEdit={effectiveCanEdit}
          isAdmin={isAdmin}
          code={code}
          onCodeChange={onCodeChange}
          onSaveCodeAndNotes={onSaveCodeAndNotes}
          isSavingCode={isSavingCode}
          suggestedCodeNumber={suggestedCodeNumber}
          isInstitutionRequest={isInstitutionRequest}

          isNewRequest={isNewRequest}
          districts={districts}
          regions={regions}
          availableYears={availableYears}
          selectedDistrictId={selectedDistrictId}
          selectedRegionId={selectedRegionId}
          selectedYear={selectedYear}
          onDistrictChange={handleDistrictChangeWithConfirm}
          onRegionChange={handleRegionChangeWithConfirm}
          onYearChange={handleYearChangeWithConfirm}
          allDropdownsFilled={allDropdownsFilled}
        />
      )}

      {/* Action buttons - only show for existing requests */}
      {request && (
        <RequestActionsBar
          request={request}
          canEdit={canEdit}
          isAdmin={isAdmin}
          isSubmitting={isSubmitting}
          ministerCount={ministers.length}
          onSubmit={onSubmit}
          onApprove={onApprove}
          onReject={() => setRejectDialogOpen(true)}
          onMarkPrinted={onMarkPrinted}
          onMarkDelivered={onMarkDelivered}
          isAdminEditMode={isAdminEditMode}
          hasUnsavedChanges={hasUnsavedChanges}
          onSaveChanges={onSaveAdminChanges}
          isSaving={isSavingAdminChanges}
          pendingChangesCount={
            (pendingChanges?.ministersAdded || 0) +
            (pendingChanges?.ministersRemoved || 0) +
            (pendingChanges?.notesChanged ? 1 : 0) +
            (pendingChanges?.codeChanged ? 1 : 0)
          }
        />
      )}

      {/* Save buttons - show for new, draft, and rejected requests */}
      {(isNewRequest || (request && ['DRAFT', 'REJECTED'].includes(request.status || ''))) && allDropdownsFilled && selectedMinisterIds.size > 0 && (
        <div className="flex justify-end gap-3">
          {/* Guardar sin enviar - solo para DRAFT/REJECTED */}
          {!isNewRequest && onSave && (
            <Button
              onClick={onSave}
              disabled={isSubmitting || !hasUnsavedChanges}
              size="lg"
              variant="default"
            >
              Guardar Cambios ({selectedMinisterIds.size} Ministro{selectedMinisterIds.size !== 1 ? 's' : ''})
            </Button>
          )}
          {/* Guardar borrador para nuevas solicitudes */}
          {isNewRequest && (
            <Button
              onClick={onSubmit}
              disabled={isSubmitting}
              size="lg"
              variant="default"
            >
              Guardar Borrador ({selectedMinisterIds.size} Ministro{selectedMinisterIds.size !== 1 ? 's' : ''})
            </Button>
          )}
          {/* Guardar y Enviar / Enviar */}
          <Button
            onClick={async () => {
              if (onCreateAndSubmit) {
                await onCreateAndSubmit();
              } else {
                await onSubmit();
              }
            }}
            disabled={isSubmitting}
            variant="default"
            size="lg"
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="mr-2 size-4" />
            {isNewRequest 
              ? `Guardar y Enviar Solicitud (${selectedMinisterIds.size} Ministro${selectedMinisterIds.size !== 1 ? 's' : ''})` 
              : hasUnsavedChanges
                ? `Guardar y Enviar (${selectedMinisterIds.size} Ministro${selectedMinisterIds.size !== 1 ? 's' : ''})`
                : `Enviar (${selectedMinisterIds.size} Ministro${selectedMinisterIds.size !== 1 ? 's' : ''})`
            }
          </Button>
        </div>
      )}

      {/* Ministers grid - NEW unified grid */}
      {allDropdownsFilled && (
        <div className="rounded-lg border bg-card p-6">
          <div className="mb-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Ministros</h2>
              
              {/* Inline selection summary - only for editable requests */}
              {showSegmentedControl && (
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {selectedMinisterIds.size} seleccionado{selectedMinisterIds.size !== 1 ? 's' : ''} de {availableMinisters.filter(m => m.availability_status === 'AVAILABLE').length} disponible{availableMinisters.filter(m => m.availability_status === 'AVAILABLE').length !== 1 ? 's' : ''}
                  </span>
                  {/* Filter toggle */}
                  <SegmentedControl
                    options={getMinisterFilterOptions()}
                    value={filterState}
                    onChange={setFilterState}
                  />
                </div>
              )}
              
              {/* For non-editable requests, just show count */}
              {!showSegmentedControl && (
                <span className="text-sm text-muted-foreground">
                  {ministers.length} ministro{ministers.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            
            {/* Filters - only show for new/draft/rejected requests */}
            {showSegmentedControl && (
              <div className="flex flex-wrap items-center gap-4">
                {/* District filter - for INSTITUTION/Function requests */}
                {(isInstitutionRequest || isFunctionRequest) && districts.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Label htmlFor="district-filter" className="whitespace-nowrap text-sm font-medium">
                      Distrito:
                    </Label>
                    <Select
                      value={selectedFilterDistrictId || 'all'}
                      onValueChange={(value) => setSelectedFilterDistrictId(value === 'all' ? null : value)}
                    >
                      <SelectTrigger id="district-filter" className="w-[200px]">
                        <SelectValue placeholder="Todos los distritos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los distritos</SelectItem>
                        {districts.map((district) => (
                          <SelectItem key={district.id} value={district.id}>
                            {district.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Region filter - for INSTITUTION/Function requests */}
                {(isInstitutionRequest || isFunctionRequest) && (
                  <div className="flex items-center gap-2">
                    <Label htmlFor="region-filter" className="whitespace-nowrap text-sm font-medium">
                      Región:
                    </Label>
                    <Select
                      value={selectedFilterRegionId || 'all'}
                      onValueChange={(value) => setSelectedFilterRegionId(value === 'all' ? null : value)}
                      disabled={!selectedFilterDistrictId || filterRegions.length === 0}
                    >
                      <SelectTrigger id="region-filter" className="w-[200px]">
                        <SelectValue placeholder={selectedFilterDistrictId ? "Todas las regiones" : "Seleccione distrito primero"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las regiones</SelectItem>
                        {filterRegions.map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {/* Degree filter */}
                {uniqueDegrees.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Label htmlFor="degree-filter" className="whitespace-nowrap text-sm font-medium">
                      Categoría:
                    </Label>
                    <Select
                      value={selectedDegreeId || 'all'}
                      onValueChange={(value) => setSelectedDegreeId(value === 'all' ? null : value)}
                    >
                      <SelectTrigger id="degree-filter" className="w-[200px]">
                        <SelectValue placeholder="Todas las categorías" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas las categorías</SelectItem>
                        {uniqueDegrees.map((degree) => (
                          <SelectItem key={degree.id} value={degree.id}>
                            {degree.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>

          {isLoadingMinisters ? (
            <div className="flex h-48 items-center justify-center">
              <InlineLoader message="Cargando ministros..." />
            </div>
          ) : filteredMinisters.length === 0 && searchValue.trim() === '' ? (
            <div className="flex h-48 items-center justify-center">
              <p className="text-gray-500">
                {filterState === 'selected' 
                  ? 'No hay ministros seleccionados' 
                  : filterState === 'available'
                  ? 'No hay ministros disponibles'
                  : 'No hay ministros para mostrar'}
              </p>
            </div>
          ) : (
            <DataTableView<MinisterAvailability>
              data={paginatedMinisters}
              columns={columns}
              currentPage={isInstitutionRequest ? currentPage : 1}
              totalPages={isInstitutionRequest ? totalPages : 1}
              totalItems={filteredMinisters.length}
              pageSize={isInstitutionRequest ? pageSize : filteredMinisters.length}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 20, 50, 100]}
              searchValue={searchValue}
              onSearchChange={(value) => {
                setSearchValue(value);
              }}
              searchPlaceholder="Buscar por nombre, código ADB o CI..."
              emptyMessage={filteredMinisters.length === 0 && searchValue.trim() !== '' ? "No se encontraron ministros que coincidan con la búsqueda" : "No hay ministros para mostrar"}
              isLoading={false}
              onRowClick={handleRowClick}
              getRowClassName={(row) => {
                // Only highlight rows for editable requests (DRAFT/REJECTED)
                if (request && !['DRAFT', 'REJECTED'].includes(request.status || '')) {
                  return ''; // No highlighting for active requests
                }
                return selectedMinisterIds.has(row.id) ? 'bg-primary/20 hover:bg-primary/30' : '';
              }}
              hidePagination={!isInstitutionRequest}
            />
          )}
        </div>
      )}

      {/* Remove minister dialog */}
      <RemoveMinisterDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        minister={ministerToRemove}
        isSubmitting={isSubmitting}
        onConfirm={handleRemoveMinister}
      />

      {/* Reject request dialog */}
      <RejectRequestDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        isSubmitting={isSubmitting}
        onConfirm={handleRejectRequest}
      />
      
      {/* Unsaved changes dialog */}
      <UnsavedChangesDialog
        open={unsavedChangesDialogOpen}
        onOpenChange={setUnsavedChangesDialogOpen}
        onConfirm={handleUnsavedChangesConfirm}
        onCancel={handleUnsavedChangesCancel}
        pendingChanges={pendingChanges}
      />
      
      {/* Dropdown change confirmation dialog */}
      <DropdownChangeConfirmDialog
        open={dropdownChangeDialogOpen}
        onOpenChange={setDropdownChangeDialogOpen}
        onConfirm={handleDropdownChangeConfirm}
        ministerCount={selectedMinisterIds.size}
      />
      
      {/* Scroll to top button */}
      {showScrollTop && (
        <Button
          onClick={scrollToTop}
          size="icon"
          className="fixed bottom-6 right-6 z-50 size-12 rounded-full shadow-lg transition-opacity hover:shadow-xl"
          title="Volver arriba"
        >
          <ArrowUp className="size-5" />
        </Button>
      )}
      
      {/* Loading backdrop when saving/submitting */}
      {(isSubmitting || isSavingAdminChanges) && (
        <FullPageLoader message={isSubmitting ? "Procesando solicitud..." : "Guardando cambios..."} />
      )}
    </div>
  );
}
